export const splitHero = {
  breadcrumb: "Rinnai Split Systems",
  eyebrow: "RINNAI INVERTER SPLIT SYSTEMS · PERTH WA",
  title: "Rinnai split system air conditioning,",
  highlight: "for the rooms you live in.",
  intro: "A Rinnai inverter split system is the most direct way to heat and cool a bedroom, living area or home office. We help you choose the right series and size, then install it neatly with the indoor unit, outdoor unit and pipe run properly planned.",
  image: "https://cdn.magicpatterns.com/patterns/generated-images/5c1c3737-939c-4098-b1a8-18a6c6c7a9b8.jpg",
  imageAlt: "Calm Perth bedroom with a white wall-mounted split system air conditioner",
  facts: ["2.5kW to 9.0kW sizes", "Wi-Fi and voice control", "7 year Rinnai warranty*"],
};

export const splitQuickAnswer = {
  question: "What size split system do I need?",
  answer: "As a guide, bedrooms usually need 2.5kW to 3.5kW, living areas 5.0kW to 7.0kW and large open-plan spaces 8.0kW or more. Room size, ceiling height, insulation, window size and afternoon sun all change the answer, so we confirm it with a heat load assessment before you buy.",
  points: [
    { label: "Bedrooms", value: "2.5kW to 3.5kW" },
    { label: "Living areas", value: "5.0kW to 7.0kW" },
    { label: "Large open plan", value: "8.0kW and above" },
    { label: "Perth energy zone", value: "Average climate zone" },
    { label: "Refrigerant", value: "R32 (TX Series)" },
    { label: "Warranty", value: "7 years on eligible systems*" },
  ],
};

export const splitFeatures = [
  {
    title: "Built-in Wi-Fi and voice control",
    text: "Switch on, set the temperature and adjust fan speed through the NetHome Plus app, or by voice with compatible Google Assistant and Amazon Alexa devices.",
  },
  {
    title: "Healthier indoor air",
    text: "A Cold Catalyst filter and HEPA filter reduce up to 99% of bacteria and dust particles, as tested by independent laboratory Intertek.",
  },
  {
    title: "GoldGuard coastal protection",
    text: "The outdoor coil’s GoldGuard coating resists oxidation and corrosion better than standard coils, a real advantage near Perth’s coast.",
  },
  {
    title: "Quiet, balanced airflow",
    text: "Indoor sound pressure starts from 19.5 dB(A) on the 3.5kW model, and 3D airflow swings vertically and horizontally for even comfort.",
  },
  {
    title: "Sleep, timer and turbo modes",
    text: "Sleep mode adjusts gently overnight, the timer runs from 30 minutes to 24 hours and Turbo cools or warms the room quickly.",
  },
  {
    title: "Demand control and R32",
    text: "DRED-enabled to help reduce power use at peak times, with R32 refrigerant for lower global warming potential.",
  },
];

export const splitPerthLabel = {
  eyebrow: "PERTH CLIMATE ZONE",
  title: "Read the energy label the way Perth homes should.",
  text: "Every new split system carries a Zoned Energy Rating Label with ratings for hot, average and cold climates. Perth sits in the average zone, so that is the row to compare. We explain what each rating means for your room and your power bill.",
  points: [
    "Cooling capacity at 35°C outdoors",
    "Heating capacity at 7°C and 2°C",
    "Estimated annual energy use (kWh)",
    "Indoor and outdoor noise levels",
  ],
  image: "https://cdn.magicpatterns.com/patterns/generated-images/243a6514-5f44-4d6d-9fbc-18fb3e3e02e0.jpg",
  imageAlt: "Bright open-plan Perth living area with a wall-mounted air conditioner",
};

export const splitSizing = {
  columns: ["TX Series model", "Nominal capacity", "Suitable room size*", "Cooling stars (Perth zone)", "Heating stars (Perth zone)", "Quietest indoor level"],
  rows: [
    ["HSNRTX25", "2.5kW", "11-17m²", "5.0", "3.5", "20.0 dB(A)"],
    ["HSNRTX35", "3.5kW", "16-23m²", "4.5", "3.0", "19.5 dB(A)"],
    ["HSNRTX50", "5.0kW", "23-33m²", "4.0", "2.5", "20.0 dB(A)"],
    ["HSNRTX60", "6.0kW", "27-40m²", "3.5", "2.5", "20.0 dB(A)"],
    ["HSNRTX70", "7.0kW", "32-47m²", "3.5", "2.5", "21.0 dB(A)"],
    ["HSNRTX80", "7.6kW", "36-53m²", "3.5", "2.5", "21.0 dB(A)"],
    ["HSNRTX90", "9.0kW", "36-53m²", "4.0", "2.5", "22.5 dB(A)"],
  ],
};

export const splitRange = [
  {
    title: "TX Series",
    text: "HEPA filtration, GoldGuard coastal protection and built-in Wi-Fi with voice control, from 2.5kW to 9.0kW.",
  },
  {
    title: "PX Series",
    text: "Up to 7 star efficiency, ECO+ AI energy saving, a smart human sensor and Turbo+ for rapid comfort.",
  },
  {
    title: "VX Series",
    text: "Up to 6.5 star efficiency, long-distance airflow up to 12m, a self-cleaning function and Wi-Fi with voice control.",
  },
  {
    title: "T Series",
    text: "Up to 9 star efficiency, airflow up to 10m, demand control and Wi-Fi with voice control.",
  },
  {
    title: "PB Series",
    text: "Up to 6.5 star efficiency, Wi-Fi control, demand control and quiet operation for everyday comfort.",
  },
  {
    title: "CJ and CJ1 Series",
    text: "Cooling-only models available in WA, with up to 8.5 or 9 star efficiency and Wi-Fi with voice control.",
  },
];

export const splitProsCons = {
  benefits: [
    "Energy-efficient inverter operation",
    "Heating and cooling in one system",
    "Quiet performance for bedrooms and studies",
    "Simpler, faster installation than ducted",
    "Individual control of each room",
    "Rapid comfort with Turbo mode",
  ],
  considerations: [
    "Best suited to single rooms or living zones",
    "The indoor unit is visible on the wall",
    "Professional installation is required",
    "Filters need regular cleaning",
    "Correct sizing matters for efficiency and comfort",
    "Several rooms may suit multi-split or ducted better",
  ],
};

export const splitComparison = {
  columns: ["", "Split system", "Multi-split", "Ducted"],
  rows: [
    ["Best for", "One room or living zone", "A few rooms, one outdoor unit", "Whole-home comfort"],
    ["Indoor units", "One visible wall unit", "One visible unit per room", "Hidden in the roof, ceiling outlets only"],
    ["Outdoor units", "One per indoor unit", "One shared outdoor unit", "One outdoor unit"],
    ["Room control", "Per unit", "Independent per room", "Zoning with Rinnai Home or iZone"],
    ["Upfront cost", "Lowest", "Moderate", "Highest"],
    ["Installation", "Simplest", "Moderate", "Needs roof space and duct design"],
  ],
};
